import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './Hero.css';

// Hero images - gallery images showcasing the predgy lifestyle
const heroImages = [
  {
    src: '/gallery/openart-aiden-rourke-is-ardently-giving-a-speech-on-stage-mic-in-hand-wearing-a-blv-neck-sweater-and-black-chelsea-boots-frontal-side-shot-as-he-is-talking_hy5y9y_k_upscaled.jpg',
    alt: 'Speaking on stage in V-neck sweater and Chelsea boots',
    caption: 'Command The Room'
  },
  {
    src: '/gallery/openart-aiden-rourke-is-casually-walking-by-himself-on-lands-end-trail-in-sf-wearilooking-carefree-staring-aimlessly-into-the-distance-with-a-straight-face_Mal3wbzz_upscaled.jpg',
    alt: 'Casual walk on Lands End Trail',
    caption: 'Rugged Freedom'
  },
  {
    src: '/gallery/openart-aiden-rourke-is-working-on-his-asus-rog-zephyrus-16-silver-sitting-down-boblue-jeans-and-black-chelsea-boots-there-is-no-one-around-him-in-the-cafe_V6tTFjxJ_upscaled.jpg',
    alt: 'Working in a cafe with refined casual style',
    caption: 'Effortless Edge'
  }
];

const Hero = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  useEffect(() => {
    if (!isAutoPlaying) return;
    
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroImages.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [isAutoPlaying]);

  const handleSlideChange = (index) => {
    setCurrentSlide(index);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  const nextSlide = () => {
    handleSlideChange((currentSlide + 1) % heroImages.length);
  };

  const prevSlide = () => {
    handleSlideChange((currentSlide - 1 + heroImages.length) % heroImages.length);
  };

  return (
    <section id="hero" className="hero">
      {/* Background Pattern */}
      <div className="hero-pattern"></div>
      
      {/* Gallery */}
      <div className="hero-gallery">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentSlide}
            className="hero-slide"
            initial={{ opacity: 0, scale: 1.1 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
          >
            <div className="slide-overlay"></div>
            <img 
              src={process.env.PUBLIC_URL + heroImages[currentSlide].src} 
              alt={heroImages[currentSlide].alt}
            />
            <motion.span 
              className="slide-caption"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.5 }}
            >
              {heroImages[currentSlide].caption}
            </motion.span>
          </motion.div>
        </AnimatePresence>

        {/* Navigation Arrows */}
        <button className="gallery-nav prev" onClick={prevSlide} aria-label="Previous slide">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </button>
        <button className="gallery-nav next" onClick={nextSlide} aria-label="Next slide">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M9 18l6-6-6-6" />
          </svg>
        </button>

        {/* Slide Indicators */}
        <div className="slide-indicators">
          {heroImages.map((_, index) => (
            <button
              key={index}
              className={`indicator ${index === currentSlide ? 'active' : ''}`}
              onClick={() => handleSlideChange(index)}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </div>

      {/* Hero Content */}
      <div className="hero-content">
        <motion.div
          className="hero-text"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
        >
          <span className="hero-eyebrow">Introducing</span>
          <h1 className="hero-title">
            <span className="title-line">SLATE</span>
            <span className="title-ampersand">&</span>
            <span className="title-line">STEEL</span>
          </h1>
          <div className="hero-tagline">
            <div className="tagline-line"></div>
            <p>Rugged Refinement</p>
            <div className="tagline-line"></div>
          </div>
          <p className="hero-description">
            Where prep meets edge. Where quality meets character.<br />
            Dress like you mean business — and mean it.
          </p>
          <div className="hero-cta">
            <button 
              className="cta-primary"
              onClick={() => document.getElementById('catalog').scrollIntoView({ behavior: 'smooth' })}
            >
              Explore Collection
            </button>
            <button 
              className="cta-secondary"
              onClick={() => document.getElementById('philosophy').scrollIntoView({ behavior: 'smooth' })}
            >
              Our Philosophy
            </button>
          </div>
        </motion.div>
      </div>

      {/* Scroll Indicator */}
      <motion.div 
        className="scroll-indicator"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5, duration: 0.5 }}
      >
        <span>Scroll</span>
        <div className="scroll-line">
          <div className="scroll-dot"></div>
        </div>
      </motion.div>
    </section>
  );
};

export default Hero;
